import { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Users, CalendarDays, Sparkles, LogOut, RefreshCw, Trash2, ShieldCheck, PhoneCall, ArrowLeft,
} from 'lucide-react';
import Logo from '../components/Logo';
import { waLink } from '../lib/lead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: 'New', cls: 'bg-brand-blue/10 text-brand-blue border-brand-blue/20' },
  contacted: { label: 'Contacted', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  converted: { label: 'Converted', cls: 'bg-green-50 text-green-700 border-green-200' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const GoogleGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.3 44 30.1 44 24c0-1.3-.1-2.6-.4-3.9z" />
  </svg>
);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? 'ok' : 'checking');
  const [authError, setAuthError] = useState(location.state?.authError || '');
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState('');

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        setAuthState('ok');
      })
      .catch(() => setAuthState('anon'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeads = () => {
    return axios
      .get(`${API}/admin/leads`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) setAuthState('anon');
      });
  };

  useEffect(() => {
    if (authState === 'ok') loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const handleGoogle = () => {
    setAuthError('');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    setData(null);
    setAuthState('anon');
  };

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const r = await axios.patch(`${API}/admin/leads/${id}`, { status }, { withCredentials: true });
      setData((prev) => ({
        ...prev,
        leads: prev.leads.map((l) => (l.id === id ? r.data : l)),
        new: r.data.status === 'new' ? prev.new + 1 : prev.new - (data.leads.find((l) => l.id === id)?.status === 'new' ? 1 : 0),
      }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not update lead');
    } finally {
      setBusyId('');
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true });
      setData((prev) => ({ ...prev, total: prev.total - 1, leads: prev.leads.filter((l) => l.id !== id) }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not delete lead');
    } finally {
      setBusyId('');
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return filter === 'all' ? data.leads : data.leads.filter((l) => l.status === filter);
  }, [data, filter]);

  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bluedark" data-testid="admin-loading">
        <span className="w-8 h-8 rounded-full border-[3px] border-white/20 border-t-brand-orange animate-spin" />
      </div>
    );
  }

  if (authState !== 'ok') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bluedark grain relative px-4" data-testid="admin-login">
        <div className="absolute inset-0 hero-grid" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="relative z-10 w-full max-w-md rounded-2xl bg-white p-8 sm:p-10 text-center lead-card"
        >
          <Logo size={56} />
          <h1 className="mt-6 font-display text-3xl font-bold text-brand-ink">Settalkar Admin</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Private lead panel. Sign in with your authorized Google account to view and manage challan leads.
          </p>
          {authError && (
            <p
              className="mt-5 text-xs font-semibold text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
              data-testid="admin-auth-error"
            >
              {authError}
            </p>
          )}
          <button
            onClick={handleGoogle}
            className="mt-6 w-full h-[52px] inline-flex items-center justify-center gap-3 rounded-xl border-2 border-slate-200 bg-white hover:border-brand-blue hover:shadow-lg hover:shadow-brand-blue/10 font-bold text-brand-ink text-[15px] transition-all hover:-translate-y-0.5"
            data-testid="admin-google-signin"
          >
            <GoogleGlyph /> Continue with Google
          </button>
          <p className="mt-5 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-slate-400">
            <ShieldCheck size={13} className="text-brand-blue" /> Only allowlisted admin accounts can enter
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold text-brand-blue hover:text-brand-orange transition-colors"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={13} /> Back to settalkar.com
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size={38} />
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-brand-blue/10 text-brand-blue text-[11px] font-bold uppercase tracking-widest px-2.5 py-1">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex text-xs font-bold text-slate-500 hover:text-brand-blue transition-colors"
              data-testid="admin-view-site"
            >
              View site
            </Link>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-200" referrerPolicy="no-referrer" />
            ) : null}
            <span className="hidden md:block text-xs font-semibold text-slate-600 max-w-[180px] truncate">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-600 text-xs font-bold px-3.5 py-2 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-brand-ink">Lead Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">Every challan request submitted on settalkar.com, newest first.</p>
          </div>
          <button
            onClick={loadLeads}
            className="inline-flex items-center gap-2 self-start sm:self-auto rounded-xl bg-brand-blue hover:bg-brand-bluedark text-white text-xs font-bold px-4 py-2.5 transition-colors"
            data-testid="admin-refresh-button"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Users, label: 'Total Leads', value: data?.total ?? '—', tint: 'text-brand-blue bg-brand-blue/10' },
            { icon: CalendarDays, label: 'Leads Today', value: data?.today ?? '—', tint: 'text-brand-orange bg-brand-orange/10' },
            { icon: Sparkles, label: 'New (Unactioned)', value: data?.new ?? '—', tint: 'text-green-600 bg-green-50' },
          ].map(({ icon: Icon, label, value, tint }) => (
            <div key={label} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${tint}`}>
                <Icon size={20} />
              </span>
              <span>
                <span className="block font-display text-3xl font-bold text-brand-ink leading-none">{value}</span>
                <span className="mt-1 block text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap gap-2" data-testid="admin-filter-row">
          {['all', 'new', 'contacted', 'converted', 'closed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize border transition-colors ${
                filter === f
                  ? 'bg-brand-ink text-white border-brand-ink'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-brand-blue hover:text-brand-blue'
              }`}
              data-testid={`admin-filter-${f}`}
            >
              {f === 'all' ? 'All Leads' : STATUS_META[f].label}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="admin-leads-table">
          {!data ? (
            <div className="py-20 flex justify-center">
              <span className="w-7 h-7 rounded-full border-[3px] border-slate-200 border-t-brand-orange animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-display text-2xl font-bold text-brand-ink">No leads here yet</p>
              <p className="mt-2 text-sm text-slate-500">New submissions from the website will appear instantly.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[900px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Lead', 'Vehicle / State', 'Contact', 'Source', 'Received', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-5 py-4">
                        <span className="block text-sm font-bold text-brand-ink">{l.full_name || '—'}</span>
                        <span className="block text-[11px] font-mono text-slate-400 mt-0.5">{l.id.slice(-8)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="block text-sm font-bold text-brand-blue font-mono">{l.vehicle_number || '—'}</span>
                        <span className="block text-xs text-slate-500 mt-0.5">{l.state_city || '—'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="block text-sm font-semibold text-brand-ink">+91 {l.phone}</span>
                        <a
                          href={waLink(`Hi ${l.full_name || 'there'}, this is the Settalkar legal desk regarding your challan ${l.vehicle_number || ''}.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#25D366] hover:text-green-700"
                          data-testid={`admin-lead-whatsapp-${l.id}`}
                        >
                          <PhoneCall size={11} /> WhatsApp now
                        </a>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold capitalize text-slate-600">
                          {l.source}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs font-semibold text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-5 py-4">
                        <select
                          value={l.status}
                          disabled={busyId === l.id}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold cursor-pointer focus:outline-none ${STATUS_META[l.status]?.cls || ''}`}
                          data-testid={`admin-lead-status-${l.id}`}
                        >
                          {Object.entries(STATUS_META).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => deleteLead(l.id)}
                          disabled={busyId === l.id}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                          aria-label="Delete lead"
                          data-testid={`admin-lead-delete-${l.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
